import React, { useCallback } from 'react';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SingleRadioButton } from 'shared/design-system/components/radio/Radiobutton';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import styled from 'styled-components';
import { updateOrganizationVisibility } from './api';
import {
  AccountVisibility,
  ReportVisibility,
} from 'shared/auth/hooks/useUserActions';
import TrueImpactFloatLoading from 'shared/design-system/components/TrueImpactFloatLoading';
import {
  loginModalSelectionOptions,
  managementPageSelectionOptions,
  reportVisibilitySubOptions,
} from './utils';

export type SharingType = 'public' | 'private' | 'custom' | undefined;
export interface ReportVisibilityOptions {
  value: NonNullable<SharingType>;
  heading: string | JSX.Element | JSX.Element[];
  subHeading: string | JSX.Element | JSX.Element[];
  disabled?: boolean;
  disabledReason?: string | JSX.Element | JSX.Element[];
}

export const getOrganizationVisibility = (
  selection: SharingType,
): AccountVisibility => {
  switch (selection) {
    case 'public':
      return 'PUBLIC_DEFAULT';
    case 'private':
      return 'RESTRICTED_DEFAULT';
    case 'custom':
      return 'CUSTOM';
    default:
      return 'SKIPPED';
  }
};

export const getOrganizationVisibilityToSharing = (
  visibility: AccountVisibility,
): SharingType | undefined => {
  switch (visibility) {
    case 'PUBLIC_DEFAULT':
      return 'public';
    case 'RESTRICTED_DEFAULT':
      return 'private';
    case 'CUSTOM':
      return 'custom';
    default:
      return undefined;
  }
};

export const getReportVisibility = (sharing: SharingType): ReportVisibility => {
  switch (sharing) {
    case 'public':
      return 'PUBLIC';
    default:
      return 'RESTRICTED';
  }
};

export const getReportSharingFromReportVisibility = (
  visibility: ReportVisibility,
): SharingType => {
  switch (visibility) {
    case 'PUBLIC':
      return 'public';
    case 'RESTRICTED':
      return 'private';
    default:
      return 'private';
  }
};

interface ReportSharingOptionsProps {
  selection: SharingType;
  subSelection: SharingType;
  className?: string;
  disabled?: boolean;
  shouldSave: boolean;
  isLoginModal: boolean;
  handleClose?: () => unknown;
  setSelection: React.Dispatch<React.SetStateAction<SharingType>>;
  setSubSelection: React.Dispatch<React.SetStateAction<SharingType>>;
}

export const MAX_WIDTH_REPORT_VISIBILITY_OPTION = 500;
const StyledDiv = styled.div`
  .radio-option.disabled,
  .sub-radio-option.disabled {
    cursor: not-allowed;
    &:hover {
      border-color: ${COLORS.grayLight};
    }
    * {
      opacity: 0.5;
    }
  }
  .disabled-reason {
    max-width: ${MAX_WIDTH_REPORT_VISIBILITY_OPTION}px;
    margin: -${SPACERS.tiny} auto ${SPACERS.small} auto;
    padding: 0 ${SPACERS.medium} 0 calc(${SPACERS.medium} + 24px);
    font-style: italic;
    color: ${COLORS.grayDark};
  }
  .radio-option,
  .sub-radio-option {
    display: flex;
    cursor: pointer;
    align-items: flex-start;
    gap: ${SPACERS.small};
    border-radius: ${SPACERS.tiny};
    border: 1px solid ${COLORS.grayBase};
    background-color: ${COLORS.whiteBase};
    transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
    .radio-option-heading {
      margin-bottom: ${SPACERS.extraTiny};
    }
    .radio-option-subheading {
      color: ${COLORS.grayDark};
    }
    .radio-option-control {
      flex-shrink: 0;
      padding-top: 2px;
    }
    &:hover {
      border-color: ${COLORS.ctaPrimary};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
  }
  .radio-option {
    padding: ${SPACERS.medium};
  }
  .sub-radio-option {
    max-width: ${MAX_WIDTH_REPORT_VISIBILITY_OPTION}px;
    margin-left: auto;
    margin-right: auto;
    padding: ${SPACERS.small} ${SPACERS.medium};
    margin-bottom: ${SPACERS.small};
    &,
    &:hover,
    &.radio-option-checked {
      box-shadow: none;
    }
  }
  .radio-option-checked,
  .radio-option-checked:hover {
    border-color: ${COLORS.ctaPrimary};
    background-color: ${COLORS.ctaPrimaryBackgroundLighter};
    box-shadow: 0 0 0 1px ${COLORS.ctaPrimary};
  }
  .radio-option-margin {
    margin-bottom: ${SPACERS.small};
  }
`;

const Radio: React.FC<{
  className?: string;
  onClick: () => unknown;
  isChecked: boolean;
  disabled?: boolean;
  heading: string | JSX.Element | JSX.Element[];
  subHeading: string | JSX.Element | JSX.Element[];
}> = ({ isChecked, heading, subHeading, onClick, className, disabled }) => {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={mergeClassNames(className, disabled && 'disabled')}
    >
      <div className="radio-option-control">
        <SingleRadioButton check={isChecked} />
      </div>
      <div>
        <TrueImpactText variant="heading" className="radio-option-heading">
          {heading}
        </TrueImpactText>
        <TrueImpactText className="radio-option-subheading">
          {subHeading}
        </TrueImpactText>
      </div>
    </div>
  );
};

export const ReportVisibilityRadioOptions: React.FC<{
  subSelection: SharingType;
  disabled?: boolean;
  options: ReportVisibilityOptions[];
  setSubSelection: (value: SharingType) => unknown;
}> = ({ options, subSelection, setSubSelection, disabled }) => {
  const handleSubSelection = useCallback(
    (value: SharingType) => {
      setSubSelection(value);
    },
    [setSubSelection],
  );
  return (
    <StyledDiv>
      {options.map((x, index) => {
        const isChecked = x.value === subSelection;
        const isLastIndex = options.length - 1 === index;
        const isDisabled = disabled || x.disabled;
        return (
          <React.Fragment key={x.value}>
            <Radio
              disabled={isDisabled}
              heading={x.heading}
              isChecked={isChecked}
              onClick={() => handleSubSelection(x.value)}
              subHeading={x.subHeading}
              className={mergeClassNames(
                'sub-radio-option',
                isDisabled && 'disabled',
                isChecked && 'radio-option-checked',
                isLastIndex && 'radio-option-margin',
              )}
            />
            {x.disabled && x.disabledReason && (
              <div className="disabled-reason">{x.disabledReason}</div>
            )}
          </React.Fragment>
        );
      })}
    </StyledDiv>
  );
};

const ReportSharingOptions: React.FC<ReportSharingOptionsProps> = ({
  disabled,
  selection,
  shouldSave,
  subSelection,
  isLoginModal,
  className,
  handleClose,
  setSelection,
  setSubSelection,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest();

  const handleSave = useCallback(
    (visibility: AccountVisibility, newProgramVisibility: SharingType) => {
      if (!client || !shouldSave) return;
      const request = updateOrganizationVisibility(client, {
        visibility,
        newProgramVisibility,
        terms: true,
      });
      makeRequest(request);
      request.then(() => {
        handleClose?.();
      });
    },
    [client, shouldSave, handleClose],
  );

  const handleSelection = useCallback(
    (value: SharingType) => {
      const visibility = getOrganizationVisibility(value) || 'SKIPPED';
      handleSave(visibility, subSelection);
      setSelection(value);
      if (value === 'custom') {
        setSubSelection('public');
      }
    },
    [subSelection, setSelection, setSubSelection],
  );

  const handleSubSelection = useCallback(
    (value: SharingType) => {
      const visibility = getOrganizationVisibility(selection) || 'SKIPPED';
      setSubSelection(value);
      handleSave(visibility, value);
    },
    [selection, setSubSelection],
  );

  const options = isLoginModal
    ? loginModalSelectionOptions
    : managementPageSelectionOptions;

  return (
    <StyledDiv className={className}>
      <TrueImpactFloatLoading
        requestState={requestState}
        loadingMessage="Saving organization visibility"
        successMessage="Organization visibility saved successfully"
        errorMessage="Failed to save organization visibility"
      />
      {options.map((option, index) => {
        const isChecked = option.value === selection;
        const isLastIndex = options.length - 1 === index;
        return (
          <React.Fragment key={option.value}>
            <Radio
              disabled={disabled}
              heading={option.heading}
              isChecked={isChecked}
              onClick={() => handleSelection(option.value)}
              subHeading={option.subHeading}
              className={mergeClassNames(
                'radio-option',
                isChecked && 'radio-option-checked',
                !isLastIndex && 'radio-option-margin',
              )}
            />
            {option.value === 'custom' && (
              <TrueImpactCollapse
                show={selection === 'custom'}
                bufferHeight={16}
              >
                <ReportVisibilityRadioOptions
                  disabled={disabled}
                  options={reportVisibilitySubOptions}
                  subSelection={subSelection}
                  setSubSelection={handleSubSelection}
                />
              </TrueImpactCollapse>
            )}
          </React.Fragment>
        );
      })}
    </StyledDiv>
  );
};

export default ReportSharingOptions;
